import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmt, SURFACE_LABELS } from '../lib/format.js';
import { Icon } from '../components/Icon.js';
import { Funnel } from '../components/Funnel.js';
import { EmptyState } from '../components/EmptyState.js';

export function CampaignDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['campaign', id], queryFn: () => api.campaign(id) });
  const { data: links } = useQuery({ queryKey: ['campaign-links', id], queryFn: () => api.links(id) });

  const retry = useMutation({
    mutationFn: () => api.retryFailed(id),
    onSuccess: (r) => { setToast(`${r.retried} הודעות נשלחו שוב`); qc.invalidateQueries({ queryKey: ['campaign', id] }); setTimeout(() => setToast(null), 2600); },
  });

  if (isLoading || !data) {
    return <div className="content"><div className="skel" style={{ height: 40, width: 320, marginBottom: 24 }} /><div className="skel" style={{ height: 260 }} /></div>;
  }

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <Link className="back" to="/reports" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-dim)', fontSize: 14, textDecoration: 'none', marginBottom: 6 }}>
            <Icon name="chevronEnd" /> חזרה לדוחות
          </Link>
          <h1>{data.name}</h1>
          <div className="sub">{data.totalTargets} קבוצות · {fmt(data.sentCount)} נשלחו · {fmt(data.failedCount)} נכשלו</div>
        </div>
        {data.failedCount > 0 && (
          <button className="btn ghost" onClick={() => retry.mutate()} disabled={retry.isPending}>
            <Icon name="retry" /> שלח שוב למי שנכשל ({fmt(data.failedCount)})
          </button>
        )}
      </div>

      <div className="grid-2">
        <div className="card funnel">
          <div className="card-h" style={{ padding: 0 }}><h3>מה קרה עם ההודעה</h3><span className="meta">{data.totalTargets} קבוצות</span></div>
          <Funnel steps={data.funnel} />
        </div>
        <div className="card" style={{ padding: 22 }}>
          <div className="card-h" style={{ padding: 0, marginBottom: 12 }}><h3>תוכן ההודעה</h3></div>
          <div style={{ background: 'var(--surface-1)', borderRadius: 'var(--r-sm)', padding: 16, fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{data.content.text ?? '—'}</div>
        </div>
      </div>

      <div className="section-title"><h2 style={{ fontSize: 20 }}>פירוט לפי קבוצה</h2><span className="rule" /></div>
      <div className="card" style={{ overflow: 'hidden' }}>
        {data.perTarget.length === 0 ? (
          <EmptyState icon="checkDouble" title="עדיין אין נתונים" sub="הפירוט לכל קבוצה יופיע כאן ברגע שההודעה תתחיל להישלח." />
        ) : (
          <table className="dtable">
            <thead><tr><th>קבוצה</th><th>סוג</th><th>נשלח</th><th>הגיע</th><th>נקרא</th><th>נכשל</th></tr></thead>
            <tbody>
              {data.perTarget.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{r.targetName}</td>
                  <td style={{ color: 'var(--text-dim)', fontSize: 13 }}>{SURFACE_LABELS[r.surface]}</td>
                  <td className="mono">{fmt(r.sent)}</td>
                  <td className="mono">{fmt(r.delivered)}</td>
                  <td className="mono">{fmt(r.read)}</td>
                  <td className="mono" style={{ color: r.failed ? 'var(--risk)' : 'var(--text-dim)' }}>{fmt(r.failed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {links && links.totalLinks > 0 && (
        <>
          <div className="section-title"><h2 style={{ fontSize: 20 }}>לחיצות על קישורים</h2><span className="rule" /></div>
          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-h"><h3>{fmt(links.totalClicks)} לחיצות · {fmt(links.totalLinks)} קישורים</h3><Link className="meta" to="/reports/clicks" style={{ textDecoration: 'none', color: 'var(--text-dim)' }}>לכל הקישורים ←</Link></div>
            <table className="dtable">
              <thead><tr><th>קוד</th><th>כתובת מקורית</th><th>קבוצה</th><th>לחיצות</th></tr></thead>
              <tbody>
                {links.links.map((l) => (
                  <tr key={l.id}>
                    <td className="mono ltr">{l.code}</td>
                    <td className="ltr" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-dim)', fontSize: 13 }}>{l.originalUrl}</td>
                    <td>{l.targetName ?? '—'}</td>
                    <td className="mono" style={{ fontWeight: 600 }}>{fmt(l.clicks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {toast && <div className="toast"><span className="d" /> {toast}</div>}
    </div>
  );
}
